import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import AvatarUpload from '../components/AvatarUpload';
import BusinessProfile from '../components/BusinessProfile';
import { motion } from 'framer-motion';
import { Globe, TrendingUp, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://topnationapp-1-api.onrender.com';
const API = `${BACKEND_URL}/api`;

function extractCountriesArray(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.countries)) return data.countries;
  return [];
}

const Dashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/');
      return;
    }

    if (user) {
      fetchUserCountries();
      fetchProfile();
    }
  }, [user, authLoading, navigate]);

  const fetchUserCountries = async () => {
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        setCountries([]);
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API}/user/countries`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setDashboardData(response.data);
      setCountries(extractCountriesArray(response.data));

    } catch (error) {
      console.error("Dashboard error:", error);
      toast.error('Failed to load your countries');
      setCountries([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await axios.get(`${API}/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setProfile(res.data);

    } catch (e) {
      console.error("Profile error:", e);
    }
  };

  const openLink = (url) => {
    if (!url) return;
    let fixed = url.trim();
    if (!fixed.startsWith("http")) fixed = "https://" + fixed;
    window.open(fixed, "_blank", "noopener,noreferrer");
  };

  if (authLoading || loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#050505]">
        <div className="text-[#FFD700] text-2xl font-['Cinzel']">Loading...</div>
      </div>
    );
  }

 const safeCountries = Array.isArray(countries) ? countries : [];

const totalInvestment = safeCountries.reduce(
  (sum, country) => sum + (Number(country.your_total) || 0),
  0
);

const totalCountries = safeCountries.length;

const isMobile = window.innerWidth < 768;

return (
  <div className="h-screen bg-[#050505] relative overflow-hidden flex justify-center">

    <div style={{
      transform: isMobile ? "scale(1)" : "scale(0.6)",
      transformOrigin: "top center",
      width: "100%",
      maxWidth: isMobile ? "100%" : 1400
    }}>

        <Navbar />

        <main className="h-[calc(100vh-70px)] overflow-y-auto max-w-7xl mx-auto px-4 pt-4 pb-40">

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>

            <h1 className="text-3xl md:text-4xl font-bold text-[#FFD700] mb-6">
              Your Empire
            </h1>

            {user && (
              <>
                <div
                  className="text-center mb-4 text-[#FFD700] font-['Cinzel'] text-2xl md:text-3xl font-bold"
                  style={{
                    textShadow: `0 0 8px #FFD700, 0 0 16px #FFD700`,
                  }}
                >
                  👑 {profile?.company_name || profile?.name || user?.email || "User"}
                </div>

                <div style={{ display: "flex", gap: 10, justifyContent: "center", marginBottom: 16 }}>

                  <img src="https://cdn-icons-png.flaticon.com/512/733/733547.png"
                    onClick={() => openLink(profile?.facebook)}
                    style={iconStyle} />

                  <img src="https://cdn-icons-png.flaticon.com/512/2111/2111463.png"
                    onClick={() => openLink(profile?.instagram)}
                    style={iconStyle} />

                  <img src="https://cdn-icons-png.flaticon.com/512/3046/3046121.png"
                    onClick={() => openLink(profile?.tiktok)}
                    style={iconStyle} />

                  <img src="https://cdn-icons-png.flaticon.com/512/174/174857.png"
                    onClick={() => openLink(profile?.linkedin)}
                    style={iconStyle} />

                  <img src="https://cdn-icons-png.flaticon.com/512/733/733579.png"
                    onClick={() => openLink(profile?.twitter)}
                    style={iconStyle} />

                </div>
              </>
            )}

            <div className="glass-panel p-4 mb-6 flex justify-center">
              <AvatarUpload />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">

              <div className="glass-panel p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Globe className="w-5 h-5 text-[#FFD700]" />
                  <div className="text-xs text-gray-400">Countries Owned</div>
                </div>
                <div className="text-3xl text-white font-bold">{totalCountries}</div>
              </div>

              <div className="glass-panel p-4">
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className="w-5 h-5 text-[#FFD700]" />
                  <div className="text-xs text-gray-400">Total Investment</div>
                </div>
                <div className="text-3xl text-[#FFD700] font-bold">
                  €{(totalInvestment || 0).toFixed(2)}
                </div>
              </div>

              <div className="glass-panel p-4">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-5 h-5 text-[#FFD700]" />
                  <div className="text-xs text-gray-400">Average Bid</div>
                </div>
                <div className="text-3xl text-white font-bold">
                  {totalCountries > 0
                    ? `€${(totalInvestment / totalCountries).toFixed(2)}`
                    : '€0.00'}
                </div>
              </div>

            </div>

            <div className="glass-panel p-4">
              <h2 className="text-xl text-[#FFD700] mb-4">Your Countries</h2>

              <div style={{ maxHeight: "40vh", overflowY: "auto" }}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">

                  {safeCountries.map((country) => (
                    <div key={country.country_code}
                      className="bg-[#121212] border border-[#333] rounded-lg p-3">

                      <h3 className="text-lg text-white mb-1">
                        {country.country_name}
                      </h3>

                      <div className="text-gray-400 text-xs mb-1">
                        {country.country_code}
                      </div>

                      <div className="text-[#FFD700] text-lg font-bold">
                        €{(Number(country.your_total) || 0).toFixed(2)}
                      </div>

                    </div>
                  ))}

                </div>
              </div>
            </div>

          </motion.div>

          <div className="mt-8">
            <BusinessProfile />
          </div>

        </main>
      </div>
    </div>
  );
};

const iconStyle = {
  width: 24,
  height: 24,
  padding: 4,
  borderRadius: 6,
  background: "linear-gradient(145deg, #FFD700, #B89600)",
  boxShadow: "0 0 10px rgba(255,215,0,0.6)",
  cursor: "pointer"
};

export default Dashboard;
